// How an audit log entry's action reads on an activity feed. Most writers record readable text
// ("Intake answer updated"); some record a code ("LIFECYCLE_EVENT_COMPLETED"), which reads as a
// sentence here ("Lifecycle event completed"). Readable text passes through unchanged.

const ACRONYMS = new Set(["AI", "API", "BIA", "CE", "CSV", "DORA", "DPIA", "DSR", "JML", "MFA", "OES", "PDF", "RFI", "SAML", "SOA", "SSO", "TOTP", "TPRM"]);

export function auditActionLabel(action: string): string {
  if (!/^[A-Z0-9]+(?:_[A-Z0-9]+)+$|^[A-Z]{4,}$/.test(action)) return action;
  const words = action.split("_").map((w) => (ACRONYMS.has(w) ? w : w.toLowerCase()));
  const first = words[0];
  words[0] = ACRONYMS.has(first) ? first : first.charAt(0).toUpperCase() + first.slice(1);
  return words.join(" ");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

/**
 * A stored instant as the platform writes dates ("1 Oct 2026", or "1 Oct 2026, 10:00 UTC" when it
 * carries a time); a key such as "not_tested" as words ("Not tested"). Anything else unchanged.
 */
function readableString(value: string): string {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):\d{2}(?:\.\d+)?Z$/);
  if (m) {
    const date = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
    return m[4] === "00" && m[5] === "00" ? date : `${date}, ${m[4]}:${m[5]} UTC`;
  }
  if (/^[a-z]+(?:_[a-z0-9]+)+$/.test(value)) {
    const words = value.replace(/_/g, " ");
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return value;
}

/** A recorded field name as words: "testedAt" -> "Tested at", "frameworkIds" -> "Framework ids". */
export function readableFieldName(field: string): string {
  const words = field.replace(/[_-]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
}

/**
 * A recorded value as a person reads it: an object as "key: value" pairs (nested ones in brackets),
 * a list joined with commas, never raw JSON ("attestations: {} → {"mfa":{…}}").
 */
export function readableAuditValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(empty)";
  if (typeof value === "string") return readableString(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value === true ? "yes" : value === false ? "no" : value);
  if (Array.isArray(value)) return value.length ? value.map((x) => (x !== null && typeof x === "object" ? `(${readableAuditValue(x)})` : readableAuditValue(x))).join(", ") : "(none)";
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) return "(none)";
    return entries.map(([k, v]) => (v !== null && typeof v === "object" && !Array.isArray(v) ? `${k} (${readableAuditValue(v)})` : `${k}: ${readableAuditValue(v)}`)).join("; ");
  }
  return String(value);
}
